import { CreatedApiKey, StoreApiKey } from '@/@types/ApiKey';
import { IntegrationsTabsSection } from '@/components/Integrations/integrations-tab-section';
import Badge from '@/components/shared/Badge/Badge';
import { CustomTabs } from '@/components/shared/custom-tabs/custom-tabs';
import DialogDelete from '@/components/shared/DialogDelete/DialogDelete';
import CTATable from '@/components/shared/TableListCustom/CTATable';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { TableCell, TableRow } from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { ApiKeysService } from '@/services/api-keys.service';
import { useAuth } from '@/auth/Auth';
import useModalStore from '@/store/useModalStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Ban,
  BookOpen,
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  Plus,
  Terminal,
  Trash2,
  X,
} from 'lucide-react';
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
  CREATE_API_KEY_MODAL,
  CreateApiKeySheet,
} from './create-api-key-sheet';
import { DevelopersMcpPanel } from './developers-mcp-panel';

const DOCS_URL = 'https://docs.mundpay.com/guides/payments/introduction';
const DELETE_API_KEY_MODAL = 'delete-api-key';

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

function getDateFnsLocale(language: string) {
  if (language.startsWith('pt')) return ptBR;
  if (language.startsWith('es')) return es;
  return enUS;
}

function formatLastUsed(
  value: string | null,
  language: string
): { relative: string | null; absolute: string | null } {
  if (!value) return { relative: null, absolute: null };
  try {
    const date = parseISO(value);
    return {
      relative: formatDistanceToNow(date, {
        addSuffix: true,
        locale: getDateFnsLocale(language),
      }),
      absolute: format(date, 'dd/MM/yyyy HH:mm'),
    };
  } catch {
    return { relative: value, absolute: value };
  }
}

function maskSecret(value: string) {
  const visible = value.slice(0, 8);
  return `${visible}${'•'.repeat(Math.max(value.length - 8, 12))}`;
}

function CreatedApiKeyCard({
  createdKey,
  onDismiss,
}: {
  createdKey: CreatedApiKey;
  onDismiss: () => void;
}) {
  const { t } = useTranslation('integration');
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const copySecret = async () => {
    if (!createdKey.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      toast({
        description: t('developers.secret_copied'),
        variant: 'success',
      });
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      toast({
        description: t('developers.copy_error'),
        variant: 'destructive',
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className='mb-4 rounded-xl border border-primary/30 bg-[#18181b] p-4'
    >
      <div className='flex items-start gap-3'>
        <div className='mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary'>
          <KeyRound className='size-4' />
        </div>
        <div className='min-w-0 flex-1'>
          <strong className='block text-sm font-semibold'>
            {t('developers.secret_banner_title')}
          </strong>
          <p className='mt-1 text-xs leading-5 text-muted'>
            {t('developers.secret_banner_text', {
              name: createdKey.name || t('developers.unnamed'),
            })}
          </p>
          <div className='mt-3 flex items-center gap-2 rounded-md border border-colorBorder bg-[#09090b] px-2.5 py-2'>
            <code className='flex-1 overflow-x-auto whitespace-nowrap font-mono text-[12.5px]'>
              {revealed ? createdKey.key : maskSecret(createdKey.key)}
            </code>
            <button
              type='button'
              className='flex size-7 shrink-0 items-center justify-center rounded-md text-muted hover:bg-white/5 hover:text-white'
              onClick={() => setRevealed((current) => !current)}
              title={
                revealed
                  ? t('developers.secret_hide')
                  : t('developers.secret_show')
              }
            >
              {revealed ? (
                <EyeOff className='size-3.5' />
              ) : (
                <Eye className='size-3.5' />
              )}
            </button>
            <Button
              type='button'
              variant='secondary'
              size='sm'
              className='h-7 rounded-full'
              onClick={copySecret}
            >
              {copied ? <Check /> : <Copy />}
              {copied ? t('copied') : t('developers.copy')}
            </Button>
          </div>
        </div>
        <button
          type='button'
          className='flex size-7 shrink-0 items-center justify-center rounded-md text-muted hover:bg-white/5 hover:text-white'
          onClick={onDismiss}
          title={t('developers.dismiss')}
        >
          <X className='size-4' />
        </button>
      </div>
    </motion.div>
  );
}

export default function Developers() {
  const { t, i18n } = useTranslation(['integration', 'common']);
  const { authData } = useAuth();
  const queryClient = useQueryClient();
  const { openModal, closeModal, modals } = useModalStore();

  const [subTab, setSubTab] = useQueryState(
    'tab',
    parseAsString.withDefault('keys')
  );
  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1)
  );

  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [selectedApiKey, setSelectedApiKey] = useState<StoreApiKey | null>(null);

  const { data: apiKeys, isPending: isLoadingKeys } = useQuery({
    queryKey: ['store-api-keys', currentPage],
    queryFn: () => ApiKeysService.list(currentPage),
    enabled: Boolean(authData?.public_api_enabled),
  });

  const invalidateKeys = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['store-api-keys'],
      exact: false,
    });
  };

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) =>
      ApiKeysService.toggle(id, active),
    onSuccess: async (response) => {
      await invalidateKeys();
      toast({
        description: response.message || t('developers.updated_success'),
        variant: 'success',
      });
    },
    onError: (error: { message?: string }) => {
      toast({
        description: error?.message || t('common:data_submission_error'),
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => ApiKeysService.remove(id),
    onSuccess: async (response) => {
      setSelectedApiKey(null);
      closeModal(DELETE_API_KEY_MODAL);
      await invalidateKeys();
      toast({
        description: response.message || t('developers.deleted_success'),
        variant: 'success',
      });
    },
    onError: (error: { message?: string }) => {
      toast({
        description: error?.message || t('common:data_submission_error'),
        variant: 'destructive',
      });
    },
  });

  const headers = [
    { title: t('developers.column_name'), key: 'name' },
    { title: t('developers.column_prefix'), key: 'prefix' },
    { title: t('developers.column_status'), key: 'status' },
    { title: t('developers.column_last_used'), key: 'last_used_at' },
    { title: t('actions'), key: 'actions' },
  ];

  const renderActions = (apiKey: StoreApiKey) => (
    <CTATable
      items={[
        {
          id: 1,
          label: apiKey.active
            ? t('developers.deactivate')
            : t('developers.activate'),
          icon: apiKey.active ? <Ban /> : <Check />,
          action: () =>
            toggleMutation.mutate({ id: apiKey.id, active: !apiKey.active }),
          disabled: toggleMutation.isPending,
        },
        {
          id: 2,
          label: t('developers.delete'),
          icon: <Trash2 />,
          action: () => {
            setSelectedApiKey(apiKey);
            openModal(DELETE_API_KEY_MODAL);
          },
        },
      ]}
    />
  );

  if (!authData?.public_api_enabled) {
    return <Navigate to='/integrations/configurations' replace />;
  }

  return (
    <IntegrationsTabsSection>
      <motion.section
        className='mb-6'
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className='text-xl font-semibold'>{t('developers.title')}</h2>
        <p className='text-muted text-sm mt-2'>
          {t('developers.description')}
        </p>
      </motion.section>

      <motion.div
        className='mb-6 grid grid-cols-1 gap-4 md:grid-cols-2'
        variants={containerVariants}
        initial='hidden'
        animate='show'
      >
        <motion.button
          type='button'
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className='bg-[#18181b] hover:card-blur p-4 rounded-xl min-h-[132px] text-left'
          onClick={() => window.open(DOCS_URL, '_blank', 'noopener,noreferrer')}
        >
          <div className='flex items-center justify-between'>
            <BookOpen className='size-5 text-zinc-300' />
          </div>
          <h2 className='text-sm mt-4 mb-3'>{t('developers.docs_title')}</h2>
          <p className='text-muted text-xs -mt-2'>
            <span className='font-medium text-primary'>
              {t('developers.docs_link')}
            </span>{' '}
            {t('developers.docs_text')}
          </p>
        </motion.button>

        <motion.button
          type='button'
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          className='bg-[#18181b] hover:card-blur p-4 rounded-xl min-h-[132px] text-left'
          onClick={() => setSubTab('mcp')}
        >
          <div className='flex items-center justify-between'>
            <Terminal className='size-5 text-zinc-300' />
          </div>
          <h2 className='text-sm mt-4 mb-3'>{t('developers.mcp_card_title')}</h2>
          <p className='text-muted text-xs -mt-2'>
            {t('developers.mcp_card_text')}
          </p>
        </motion.button>
      </motion.div>

      <div className='flex flex-start mb-6'>
        <CustomTabs
          tabs={[
            { value: 'keys', label: t('developers.tab_keys') },
            { value: 'mcp', label: t('developers.tab_mcp') },
          ]}
          activeTab={subTab}
          onChange={setSubTab}
        />
      </div>

      <AnimatePresence initial={false}>
        {subTab !== 'mcp' && createdKey?.key && (
          <CreatedApiKeyCard
            key={createdKey.id}
            createdKey={createdKey}
            onDismiss={() => setCreatedKey(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode='wait'>
        {subTab === 'mcp' ? (
          <motion.div
            key='mcp'
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            <DevelopersMcpPanel />
          </motion.div>
        ) : (
          <motion.div
            key='keys'
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
          >
            <Card>
              <div className='flex flex-wrap items-center justify-between gap-3 px-5 py-4'>
                <h2 className='text-[14.5px] font-semibold'>
                  {t('developers.tab_keys')}
                </h2>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type='button'
                    onClick={() => openModal(CREATE_API_KEY_MODAL)}
                  >
                    <Plus />
                    {t('developers.create_submit')}
                  </Button>
                </motion.div>
              </div>
              <TableListCustom
                emptyDescriptionMessage={t('developers.empty_description')}
                emptyIconMessage={<KeyRound />}
                emptyTitleMessage={t('developers.empty_title')}
                headers={headers}
                isLoadingData={isLoadingKeys}
                data={apiKeys ?? { data: [] }}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                key='developers-api-keys-table'
              >
                {apiKeys?.data?.map((apiKey) => {
                  const lastUsed = formatLastUsed(
                    apiKey.last_used_at,
                    i18n.language
                  );

                  return (
                    <TableRow
                      key={apiKey.id}
                      className='h-[64px] border-b border-b-colorBorder hover:bg-transparent'
                      data-cy='table-row'
                    >
                      <TableCell className='font-medium'>
                        {apiKey.name || t('developers.unnamed')}
                      </TableCell>
                      <TableCell className='min-w-[360px] whitespace-nowrap'>
                        <code className='inline-block rounded-md border border-colorBorder bg-[#18181B] px-3 py-1.5 font-mono text-xs tracking-wide text-zinc-200'>
                          {apiKey.prefix ? `${apiKey.prefix}…` : '—'}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge
                          status={apiKey.active ? 'active' : 'inactive'}
                          text={
                            apiKey.active
                              ? t('developers.status_active')
                              : t('developers.status_inactive')
                          }
                        />
                      </TableCell>
                      <TableCell
                        className='text-sm text-zinc-300'
                        title={lastUsed.absolute ?? undefined}
                      >
                        {lastUsed.relative ?? t('developers.never_used')}
                      </TableCell>
                      <TableCell className='w-auto'>
                        {renderActions(apiKey)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableListCustom>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <CreateApiKeySheet
        onCreated={(apiKey) => {
          setCreatedKey(apiKey);
          setCurrentPage(1);
        }}
      />

      <DialogDelete
        isOpen={Boolean(modals[DELETE_API_KEY_MODAL])}
        onClose={() => {
          closeModal(DELETE_API_KEY_MODAL);
          setSelectedApiKey(null);
        }}
        onDelete={() => {
          if (!selectedApiKey) return;
          deleteMutation.mutate(selectedApiKey.id);
        }}
        title={t('developers.delete_title')}
        description={t('developers.delete_description', {
          name: selectedApiKey?.name || t('developers.unnamed'),
        })}
        isDeleting={deleteMutation.isPending}
      />
    </IntegrationsTabsSection>
  );
}
